import { useQuery } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { API_PATHS } from '@/core/api/api-paths'
import { graphqlClient } from '@/core/api/graphql-client'
import { restClient } from '@/core/api/rest-client'
import { useAuthStore } from '@/features/auth/store'
import { UserDocument } from '@/generated/graphql'
import { appConfig } from '@/shared/config/app-config'

const LINK_KEY = `${appConfig.storagePrefix}_app_banner_link`
const LINK_TTL = 24 * 60 * 60 * 1000 // 24 hours
export const STATIC_LINK = appConfig.appLinks.oneLink

// Shared state across all hook instances
let cachedLink: string | null = loadFromStorage()
let generating = false
const listeners = new Set<() => void>()

function notify() {
  for (const l of listeners) l()
}

function loadFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(LINK_KEY)
    if (!raw) return null
    const { link, expiresAt } = JSON.parse(raw)
    if (Date.now() > expiresAt) {
      localStorage.removeItem(LINK_KEY)
      return null
    }
    return link
  } catch {
    localStorage.removeItem(LINK_KEY)
    return null
  }
}

function saveToStorage(link: string) {
  localStorage.setItem(
    LINK_KEY,
    JSON.stringify({ link, expiresAt: Date.now() + LINK_TTL }),
  )
  cachedLink = link
  notify()
}

export function clearCachedLink() {
  localStorage.removeItem(LINK_KEY)
  cachedLink = null
  notify()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

function getSnapshot() {
  return cachedLink
}

function generateLink(email: string) {
  if (generating || cachedLink) return

  generating = true
  notify()

  restClient
    .post<{ link: string }>(
      API_PATHS.auth.autoLoginLink,
      { email },
      { skipAuth: true },
    )
    .then((res) => {
      saveToStorage(res.link)
    })
    .catch(() => {})
    .finally(() => {
      generating = false
      notify()
    })
}

export function useAppLink(): { link: string; isReady: boolean } {
  const stored = useSyncExternalStore(subscribe, getSnapshot, () => null)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { data: userData } = useQuery({
    queryKey: ['user'],
    queryFn: () => graphqlClient.request(UserDocument),
    enabled: isAuthenticated,
  })
  const email = userData?.me.email

  if (isAuthenticated && email && !cachedLink && !generating) {
    generateLink(email)
  }

  const link = isAuthenticated ? (stored ?? '') : STATIC_LINK
  const isReady = !isAuthenticated || !!stored

  return { link, isReady }
}
